import { Maximize2, Minimize2, Sparkles, TriangleAlert, X } from "lucide-react";
import { type ReactNode, useEffect, useRef } from "react";

import { Button } from "@krea/shared/design-system/Button";
import { Spinner } from "@krea/shared/design-system/Spinner";
import { useModalA11y } from "@krea/shared/hooks/useModalA11y";
import { cn } from "@krea/shared/utils";

import type { PanelRect } from "../CaseAssistant";
import { MORPH_DURATION_MS } from "../constants";

export interface IAssistantPanelLabels {
  expand: string;
  collapse: string;
  close: string;
  errorBody: string;
  retry: string;
  loading: string;
}

interface IAssistantPanelProps {
  isMobile: boolean;
  /** Resting rect for the desktop dock (ignored on mobile, where the sheet is full-width). */
  rect: PanelRect;
  expanded: boolean;
  companyName: string;
  orgNumber: string;
  title: string;
  ready: boolean;
  error: boolean;
  labels: IAssistantPanelLabels;
  onClose: () => void;
  onToggleExpand: () => void;
  onRetry: () => void;
  children: ReactNode;
}

export const AssistantPanel = ({
  isMobile,
  rect,
  expanded,
  companyName,
  orgNumber,
  title,
  ready,
  error,
  labels,
  onClose,
  onToggleExpand,
  onRetry,
  children,
}: IAssistantPanelProps) => {
  const panelRef = useRef<HTMLDivElement>(null);

  // Initial focus into the panel on open (ChatKit composer focus follows on ready, in CaseAssistant).
  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  // Body scroll-lock + background-inert while the mobile bottom sheet is modal. Extracted to a
  // shared hook so the next iframe-hosting panel reuses the same iframe-safe background handling.
  // (The passive focus sentinels below stay local — they're coupled to this panel's DOM/focusEdge.)
  useModalA11y(isMobile);

  const focusEdge = (edge: "first" | "last") => {
    const nodes = panelRef.current?.querySelectorAll<HTMLElement>(
      'button, [href], input, textarea, select, iframe, [tabindex]:not([tabindex="-1"])',
    );
    const list = Array.from(nodes ?? []).filter(
      (el) => !el.hasAttribute("disabled"),
    );
    if (list.length === 0) return;
    const target = edge === "first" ? list[0] : list[list.length - 1];
    target?.focus();
  };

  return (
    <>
      {/* Passive focus sentinels — MOBILE ONLY (the bottom sheet is modal). Each redirects focus only
          when the sentinel itself is focused (i.e. Tab crossed the panel edge), so they never touch
          focus while it is inside ChatKit's cross-origin iframe — unlike Radix FocusScope, which would
          steal focus back out and break the composer. Desktop is non-modal (co-use) → no trap. */}
      {isMobile && (
        // biome-ignore lint/a11y/noNoninteractiveTabindex: focus-trap sentinel
        <div tabIndex={0} aria-hidden onFocus={() => focusEdge("last")} />
      )}
      {/* Scrim only on mobile: a docked desktop panel must NOT block reading/scrolling the case
          behind it (the point of a side dock is co-use). Desktop closes via X / Esc only. */}
      {isMobile && (
        <button
          type="button"
          aria-hidden
          tabIndex={-1}
          className="fixed inset-0 z-[400001] bg-[rgba(20,12,40,0.28)] backdrop-blur-[2px]"
          onClick={onClose}
        />
      )}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal={isMobile}
        aria-label={`${title} for ${companyName}`}
        tabIndex={-1}
        // Desktop: bottom-left dock at the computed rect. Expand grows the height (bottom stays
        // anchored) and the change is CSS-transitioned, so the ChatKit iframe below is never
        // remounted. Mobile keeps the full-width bottom sheet (rect ignored).
        style={
          isMobile
            ? undefined
            : ({
                left: rect.left,
                bottom: rect.bottom,
                height: rect.height,
                width: rect.width,
                // --tw-enter-duration is the CSS variable that tailwindcss-animate's animate-in
                // utilities use for the entry animation duration. Setting it here couples the
                // desktop panel's entrance to the morph animation constant so they never drift.
                "--tw-enter-duration": `${MORPH_DURATION_MS}ms`,
              } as React.CSSProperties)
        }
        className={cn(
          "fixed z-[400002] flex flex-col overflow-hidden border border-gray-300 bg-white shadow-[0_24px_60px_-16px_rgba(0,0,0,0.32),0_6px_16px_rgba(0,0,0,0.10)]",
          "animate-in ease-[cubic-bezier(0.16,1,0.3,1)] motion-reduce:animate-none",
          isMobile
            ? "slide-in-from-bottom duration-[320ms] inset-x-0 bottom-0 h-[86vh] max-h-[760px] rounded-t-[18px] pb-[env(safe-area-inset-bottom)]"
            : "fade-in-0 slide-in-from-bottom-[40px] zoom-in-[0.98] rounded-[14px] transition-[height] motion-reduce:transition-none",
        )}
        onKeyDown={(event) => {
          if (event.key === "Escape") onClose();
        }}
      >
        {isMobile && (
          <div className="mx-auto mt-2 h-1 w-10 shrink-0 rounded-full bg-gray-400" />
        )}

        <header className="flex shrink-0 items-center gap-3 border-b border-gray-200 px-4 pb-3.5 pt-4">
          <span className="grid size-[34px] shrink-0 place-items-center rounded-[9px] bg-primary-100 text-primary">
            <Sparkles className="size-5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-section leading-tight">{title}</div>
            <div className="truncate text-xs text-gray-600">
              <b>{companyName}</b> · {orgNumber}
            </div>
          </div>
          {!isMobile && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={expanded ? labels.collapse : labels.expand}
              title={expanded ? labels.collapse : labels.expand}
              aria-pressed={expanded}
              className="size-9 rounded-lg text-gray-600 hover:bg-gray-100"
              onClick={onToggleExpand}
            >
              {expanded ? (
                <Minimize2 className="size-[18px]" />
              ) : (
                <Maximize2 className="size-[18px]" />
              )}
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={labels.close}
            className="size-9 rounded-lg text-gray-600 hover:bg-gray-100"
            onClick={onClose}
          >
            <X className="size-5" />
          </Button>
        </header>

        <div className="relative flex-1 overflow-hidden">
          {error ? (
            <div className="flex h-full flex-col items-center justify-center gap-4 px-6 text-center">
              <TriangleAlert className="size-8 text-gray-500" />
              <p className="text-base-secondary">{labels.errorBody}</p>
              <Button variant="secondary" size="sm" onClick={onRetry}>
                {labels.retry}
              </Button>
            </div>
          ) : (
            <>
              {!ready && (
                <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white">
                  <Spinner className="text-primary" size="lg" />
                  <span className="text-label">{labels.loading}</span>
                </div>
              )}
              <div className="h-full">{children}</div>
            </>
          )}
        </div>
        <p className="shrink-0 border-t border-gray-100 px-4 py-2 text-center text-[11px] leading-4 text-gray-400">
          AI-generated content can include errors.
        </p>
      </div>
      {isMobile && (
        // biome-ignore lint/a11y/noNoninteractiveTabindex: focus-trap sentinel
        <div tabIndex={0} aria-hidden onFocus={() => focusEdge("first")} />
      )}
    </>
  );
};
